import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import EpisodeCard from "@/components/feature/EpisodeCard";
import CharacterCard from "@/components/feature/CharacterCard";
import { useFavorites } from "@/hooks/useFavorites";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";

const categoryEntries = [
  {
    label: "全集剧情",
    desc: "十二集剧情逐集梳理",
    icon: "ri-film-line",
    to: "/episodes",
    color: "bg-primary-100 text-primary-700",
  },
  {
    label: "角色介绍",
    desc: "主角配角反派全档案",
    icon: "ri-user-star-line",
    to: "/characters",
    color: "bg-accent-100 text-accent-800",
  },
  {
    label: "观看指南",
    desc: "正版平台与解析文章",
    icon: "ri-compass-3-line",
    to: "/guide",
    color: "bg-secondary-100 text-secondary-800",
  },
];

export default function Home() {
  const { favorites } = useFavorites();
  const latestEpisodes = [...episodes].reverse().slice(0, 5);
  const popularCharacters = characters.slice(0, 5);

  return (
    <div className="space-y-8 md:space-y-10">
      <title>甜蜜惩罚动漫全集剧情介绍与观看指南</title>
      <meta
        name="description"
        content="甜蜜惩罚动漫网站提供详细剧情介绍、角色分析及观看指南，覆盖全集信息，帮助动漫爱好者深入了解和追踪该作品。"
      />
      <meta
        name="keywords"
        content="甜蜜惩罚动漫,甜蜜惩罚剧情,甜蜜惩罚角色,甜蜜惩罚全集,甜蜜惩罚在线观看,甜蜜惩罚介绍"
      />

      <HeroCarousel />

      <section>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {categoryEntries.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className="group flex items-center gap-4 p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className={`w-12 h-12 flex items-center justify-center rounded-lg text-2xl ${entry.color}`}>
                <i className={entry.icon}></i>
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-base text-foreground-950 group-hover:text-primary-700 transition-colors">
                  {entry.label}
                </span>
                <span className="block text-xs text-foreground-500 font-label mt-0.5">{entry.desc}</span>
              </span>
              <i className="ri-arrow-right-line ml-auto text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
            </Link>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">最新剧情</h2>
              <p className="text-sm text-foreground-500 font-label mt-1">最近更新的五集，按播出时间倒序</p>
            </div>
            <Link
              to="/episodes"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-label text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {latestEpisodes.map((episode, index) => (
              <EpisodeCard key={episode.id} episode={episode} index={index} />
            ))}
          </div>
        </section>

        <section className="lg:col-span-1">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">热门角色</h2>
              <p className="text-sm text-foreground-500 font-label mt-1">按出场顺序推荐</p>
            </div>
            <Link
              to="/characters"
              className="inline-flex items-center gap-1 text-sm font-label text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
            >
              全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4">
            {popularCharacters.map((character, index) => (
              <CharacterCard key={character.id} character={character} index={index} />
            ))}
          </div>
        </section>
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">关于这部作品</h2>
          <p className="text-sm md:text-base text-foreground-600 leading-loose font-label">
            《甜蜜惩罚》是一部围绕“甜蜜”与“惩罚”展开的都市情感番剧，讲述了甜品店女孩苏晚与顾家继承人顾时衍，
            因一枚手写书签在一个雨夜相遇，又在误解与沉默中错失、重逢，最终学会把话说出口的故事。
            本站按剧集顺序整理了全部十二集的剧情摘要与详细剧情，并为九位主要角色建立了独立的角色档案，
            你可以通过顶部的搜索框，直接输入剧情关键词或角色姓名进行检索。
          </p>
          <div className="grid grid-cols-3 gap-4 mt-6">
            {[
              { value: "12", label: "集完整剧情", icon: "ri-film-line" },
              { value: "9", label: "位角色档案", icon: "ri-group-line" },
              { value: "5", label: "篇解析文章", icon: "ri-article-line" },
            ].map((stat) => (
              <div key={stat.label} className="text-center rounded-md bg-background-100 py-4">
                <span className="block font-heading text-2xl text-primary-700">{stat.value}</span>
                <span className="block text-xs text-foreground-500 font-label mt-1 flex items-center justify-center gap-1">
                  <i className={stat.icon}></i>
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1 rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-heart-3-line"></i>
            </span>
            <h2 className="font-heading text-xl text-foreground-950">我的收藏</h2>
          </div>
          <p className="text-sm text-foreground-600 leading-relaxed font-label mb-4">
            收藏保存在本地浏览器，无需登录。你目前共收藏了{" "}
            <strong className="text-primary-700">{favorites.length}</strong> 项内容。
          </p>
          {favorites.length === 0 ? (
            <Link
              to="/episodes"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              去浏览剧情 <i className="ri-arrow-right-line"></i>
            </Link>
          ) : (
            <div className="text-sm text-foreground-600 font-label">
              在左侧「我的收藏」栏可快速跳转，或在详情页继续添加。
            </div>
          )}
        </div>
      </section>
    </div>
  );
}